import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Mail, Phone, Calendar, Eye, Trash2, Users, Car, DollarSign, CheckCircle, XCircle, X } from 'lucide-react';
import StatusBadge from '../common/StatusBadge';
import EmptyState from '../common/EmptyState';
import CompanyCharterBlock from '../charter/CompanyCharterBlock';
import CompanyPayoutAccount from './CompanyPayoutAccount';
import CompanyCommission from './CompanyCommission';
import CompanyStatementReport from './CompanyStatementReport';
import { AdminCompanyAPI, AdminDriverAPI, AdminVehicleAPI, AdminPayoutAPI, APIUtils } from '../../services/api';
import { removalSummary } from '../../utils/removalSummary';

const CompanyDetail = ({ companyId, onBack, charterVisible = false, isSuperAdmin = false }) => {
  const [company, setCompany] = useState(null);
  const [driverCount, setDriverCount] = useState(0);
  const [vehicleCount, setVehicleCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('drivers');
  const [statusNotes, setStatusNotes] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);

  const fetchCompany = useCallback(async () => {
    try {
      const response = await AdminCompanyAPI.getById(companyId);
      if (response.data.success) {
        setCompany(response.data.data.company);
        setDriverCount(response.data.data.driverCount);
        setVehicleCount(response.data.data.vehicleCount);
      }
    } catch (error) {
      console.error('Fetch company error:', APIUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const handleStatusUpdate = async (status) => {
    setStatusLoading(true);
    try {
      await AdminCompanyAPI.updateStatus(companyId, status, statusNotes);
      setStatusNotes('');
      fetchCompany();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setStatusLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Company not found</p>
        <button onClick={onBack} className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium">Back to Companies</button>
      </div>
    );
  }

  const tabs = [
    { id: 'drivers', label: 'Drivers', icon: Users, count: driverCount },
    { id: 'vehicles', label: 'Vehicles', icon: Car, count: vehicleCount },
    { id: 'payouts', label: 'Payouts', icon: DollarSign },
  ];

  return (
    <div>
      {/* Back button */}
      <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900 mb-4 text-sm font-medium">
        <ArrowLeft size={16} className="mr-1" /> Back to Companies
      </button>

      {/* Company header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: company.logoColor || '#059669' }}>
              {company.logoInitials || company.companyName?.substring(0, 2).toUpperCase()}
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-bold text-gray-900">{company.companyName}</h1>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                <span className="flex items-center"><Mail size={14} className="mr-1" />{company.email}</span>
                {company.phoneNumber && <span className="flex items-center"><Phone size={14} className="mr-1" />{company.phoneNumber}</span>}
                <span className="flex items-center"><Calendar size={14} className="mr-1" />Registered {new Date(company.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          <StatusBadge status={company.registrationStatus} />
        </div>

        {/* Info row */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-6 text-sm">
          {/* Zuther: the Zuttle operator ID, on every statement (#737). */}
          <div>
            <span className="text-gray-500">Operator ID:</span>
            <span className="ml-1 font-mono font-medium text-gray-900">{company.operatorNumber || 'Not yet assigned'}</span>
          </div>
          {company.passkey && (
            <div>
              <span className="text-gray-500">Passkey:</span>
              <span className="ml-1 font-mono font-medium text-gray-900">{company.passkey}</span>
            </div>
          )}
          <div>
            <span className="text-gray-500">Drivers:</span>
            <span className="ml-1 font-medium text-gray-900">{driverCount}</span>
          </div>
          <div>
            <span className="text-gray-500">Vehicles:</span>
            <span className="ml-1 font-medium text-gray-900">{vehicleCount}</span>
          </div>
          {company.servicesOffered?.length > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Services:</span>
              {company.servicesOffered.map((s, i) => (
                <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">{s}</span>
              ))}
            </div>
          )}
        </div>

        {/* Status actions */}
        {company.registrationStatus !== 'rejected' && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                  placeholder="Add notes for this decision..."
                />
              </div>
              {(company.registrationStatus === 'pending' || company.registrationStatus === 'suspended') && (
                <button
                  onClick={() => handleStatusUpdate('approved')}
                  disabled={statusLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 text-sm"
                >
                  {company.registrationStatus === 'suspended' ? 'Reactivate' : 'Approve'}
                </button>
              )}
              {company.registrationStatus === 'pending' && (
                <button
                  onClick={() => handleStatusUpdate('rejected')}
                  disabled={statusLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm"
                >
                  Reject
                </button>
              )}
              {company.registrationStatus === 'approved' && (
                <button
                  onClick={() => handleStatusUpdate('suspended')}
                  disabled={statusLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm"
                >
                  Suspend
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Charter (spec 6.4), only while the charter flag shows it (R77) */}
      {charterVisible && <CompanyCharterBlock companyId={companyId} />}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex space-x-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={16} className="mr-2" />
                {tab.label}
                {tab.count !== undefined && (
                  <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{tab.count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'drivers' && <CompanyDriversTab companyId={companyId} onDataChange={fetchCompany} />}
      {activeTab === 'vehicles' && <CompanyVehiclesTab companyId={companyId} onDataChange={fetchCompany} />}
      {activeTab === 'payouts' && (
        <div className="space-y-6">
          <CompanyPayoutAccount companyId={companyId} isSuperAdmin={isSuperAdmin} />
          <CompanyCommission companyId={companyId} isSuperAdmin={isSuperAdmin} />
          <CompanyStatementReport companyId={companyId} />
          <CompanyPayoutsTab companyId={companyId} />
        </div>
      )}
    </div>
  );
};

// ==================== Drivers Tab ====================
const CompanyDriversTab = ({ companyId, onDataChange }) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AdminDriverAPI.getAll({ companyId, limit: 100 });
      if (response.data.success) {
        setDrivers(response.data.data.drivers);
      }
    } catch (error) {
      console.error('Fetch drivers error:', APIUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  const handleApproval = async (driverId, approvalStatus) => {
    setActionLoading(true);
    try {
      const response = await AdminDriverAPI.updateApproval(driverId, approvalStatus, actionNotes);
      const handedBack = removalSummary(response);
      if (handedBack) alert(handedBack);
      setSelectedDriver(null);
      setActionNotes('');
      fetchDrivers();
      onDataChange();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async (driverId) => {
    setActionLoading(true);
    try {
      const response = await AdminDriverAPI.deactivate(driverId, actionNotes);
      const handedBack = removalSummary(response);
      if (handedBack) alert(handedBack);
      setSelectedDriver(null);
      setActionNotes('');
      fetchDrivers();
      onDataChange();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (driverId) => {
    setActionLoading(true);
    try {
      await AdminDriverAPI.reactivate(driverId, actionNotes);
      setSelectedDriver(null);
      setActionNotes('');
      fetchDrivers();
      onDataChange();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (driverId) => {
    if (!window.confirm('Are you sure you want to remove this driver? This cannot be undone.')) return;
    setActionLoading(true);
    try {
      const response = await AdminDriverAPI.delete(driverId, actionNotes);
      const handedBack = removalSummary(response);
      if (handedBack) alert(handedBack);
      setSelectedDriver(null);
      setActionNotes('');
      fetchDrivers();
      onDataChange();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div></div>;
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {drivers.length === 0 ? (
          <EmptyState title="No drivers" message="This company has no registered drivers." icon={Users} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approval</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {drivers.map((driver) => (
                  <tr key={driver._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{driver.name} {driver.surname}</p>
                      <p className="text-sm text-gray-500">{driver.email}</p>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-700">{driver.employeeCode}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {driver.assignedVehicle ? `${driver.assignedVehicle.vehicleMake} ${driver.assignedVehicle.model}` : 'None'}
                    </td>
                    <td className="px-6 py-4"><StatusBadge status={driver.approvalStatus || 'pending'} /></td>
                    <td className="px-6 py-4"><StatusBadge status={driver.status || 'offline'} /></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button onClick={() => setSelectedDriver(driver)} className="text-emerald-600 hover:text-emerald-800" title="View details"><Eye size={18} /></button>
                        {driver.approvalStatus === 'pending' && (
                          <>
                            <button onClick={() => { if (window.confirm(`Approve ${driver.name} ${driver.surname}?`)) handleApproval(driver._id, 'approved'); }} disabled={actionLoading} className="text-green-600 hover:text-green-800 disabled:opacity-50" title="Approve"><CheckCircle size={18} /></button>
                            <button onClick={() => { if (window.confirm(`Reject ${driver.name} ${driver.surname}?`)) handleApproval(driver._id, 'rejected'); }} disabled={actionLoading} className="text-red-600 hover:text-red-800 disabled:opacity-50" title="Reject"><XCircle size={18} /></button>
                          </>
                        )}
                        {driver.approvalStatus === 'approved' && driver.status !== 'inactive' && (
                          <button onClick={() => { if (window.confirm(`Deactivate ${driver.name} ${driver.surname}?`)) handleDeactivate(driver._id); }} disabled={actionLoading} className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50">Deactivate</button>
                        )}
                        {driver.approvalStatus === 'approved' && driver.status === 'inactive' && (
                          <button onClick={() => { if (window.confirm(`Reactivate ${driver.name} ${driver.surname}?`)) handleReactivate(driver._id); }} disabled={actionLoading} className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-50">Reactivate</button>
                        )}
                        {(driver.status === 'inactive' || driver.approvalStatus === 'rejected') && (
                          <button onClick={() => handleDelete(driver._id)} disabled={actionLoading} className="text-red-500 hover:text-red-700 disabled:opacity-50" title="Remove"><Trash2 size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Driver Modal */}
      {selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{selectedDriver.name} {selectedDriver.surname}</h3>
              <button onClick={() => { setSelectedDriver(null); setActionNotes(''); }} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">Email</p><p className="font-medium">{selectedDriver.email}</p></div>
                <div><p className="text-sm text-gray-500">Phone</p><p className="font-medium">{selectedDriver.phoneNumber}</p></div>
                <div><p className="text-sm text-gray-500">Employee Code</p><p className="font-medium font-mono">{selectedDriver.employeeCode}</p></div>
                <div><p className="text-sm text-gray-500">Approval</p><StatusBadge status={selectedDriver.approvalStatus || 'pending'} /></div>
                <div><p className="text-sm text-gray-500">Status</p><StatusBadge status={selectedDriver.status || 'offline'} /></div>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none" rows={2} placeholder="Add notes..." />
                <div className="flex space-x-3 mt-3">
                  {selectedDriver.approvalStatus === 'pending' && (
                    <>
                      <button onClick={() => handleApproval(selectedDriver._id, 'approved')} disabled={actionLoading} className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 text-sm">Approve</button>
                      <button onClick={() => handleApproval(selectedDriver._id, 'rejected')} disabled={actionLoading} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm">Reject</button>
                    </>
                  )}
                  {selectedDriver.approvalStatus === 'approved' && selectedDriver.status !== 'inactive' && (
                    <button onClick={() => handleDeactivate(selectedDriver._id)} disabled={actionLoading} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm">Deactivate</button>
                  )}
                  {selectedDriver.approvalStatus === 'approved' && selectedDriver.status === 'inactive' && (
                    <button onClick={() => handleReactivate(selectedDriver._id)} disabled={actionLoading} className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 text-sm">Reactivate</button>
                  )}
                  {(selectedDriver.status === 'inactive' || selectedDriver.approvalStatus === 'rejected') && (
                    <button onClick={() => handleDelete(selectedDriver._id)} disabled={actionLoading} className="flex-1 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 font-medium disabled:opacity-50 text-sm">Remove</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== Vehicles Tab ====================
const CompanyVehiclesTab = ({ companyId, onDataChange }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AdminVehicleAPI.getAll({ companyId, limit: 100 });
      if (response.data.success) {
        setVehicles(response.data.data.vehicles);
      }
    } catch (error) {
      console.error('Fetch vehicles error:', APIUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const handleApproval = async (vehicleId, approvalStatus) => {
    setActionLoading(true);
    try {
      await AdminVehicleAPI.updateApproval(vehicleId, approvalStatus, actionNotes);
      setSelectedVehicle(null);
      setActionNotes('');
      fetchVehicles();
      onDataChange();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async (vehicleId) => {
    setActionLoading(true);
    try {
      await AdminVehicleAPI.deactivate(vehicleId, actionNotes);
      setSelectedVehicle(null);
      setActionNotes('');
      fetchVehicles();
      onDataChange();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (vehicleId) => {
    setActionLoading(true);
    try {
      await AdminVehicleAPI.reactivate(vehicleId, actionNotes);
      setSelectedVehicle(null);
      setActionNotes('');
      fetchVehicles();
      onDataChange();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to remove this vehicle? This cannot be undone.')) return;
    setActionLoading(true);
    try {
      await AdminVehicleAPI.delete(vehicleId, actionNotes);
      setSelectedVehicle(null);
      setActionNotes('');
      fetchVehicles();
      onDataChange();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div></div>;
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {vehicles.length === 0 ? (
          <EmptyState title="No vehicles" message="This company has no registered vehicles." icon={Car} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">License Plate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approval</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vehicles.map((vehicle) => (
                  <tr key={vehicle._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{vehicle.year} {vehicle.vehicleMake} {vehicle.model}</p>
                      <p className="text-sm text-gray-500">{vehicle.color} - {vehicle.capacity} seats</p>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-700">{vehicle.licensePlate}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 capitalize">{vehicle.vehicleType}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {vehicle.assignedDriver ? `${vehicle.assignedDriver.name} ${vehicle.assignedDriver.surname}` : 'Unassigned'}
                    </td>
                    <td className="px-6 py-4"><StatusBadge status={vehicle.approvalStatus || 'pending'} /></td>
                    <td className="px-6 py-4"><StatusBadge status={vehicle.status || 'active'} /></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button onClick={() => setSelectedVehicle(vehicle)} className="text-emerald-600 hover:text-emerald-800" title="View details"><Eye size={18} /></button>
                        {vehicle.approvalStatus === 'pending' && (
                          <>
                            <button onClick={() => { if (window.confirm(`Approve ${vehicle.vehicleMake} ${vehicle.model} (${vehicle.licensePlate})?`)) handleApproval(vehicle._id, 'approved'); }} disabled={actionLoading} className="text-green-600 hover:text-green-800 disabled:opacity-50" title="Approve"><CheckCircle size={18} /></button>
                            <button onClick={() => { if (window.confirm(`Reject ${vehicle.vehicleMake} ${vehicle.model} (${vehicle.licensePlate})?`)) handleApproval(vehicle._id, 'rejected'); }} disabled={actionLoading} className="text-red-600 hover:text-red-800 disabled:opacity-50" title="Reject"><XCircle size={18} /></button>
                          </>
                        )}
                        {vehicle.approvalStatus === 'approved' && vehicle.status === 'active' && (
                          <button onClick={() => { if (window.confirm(`Deactivate ${vehicle.vehicleMake} ${vehicle.model} (${vehicle.licensePlate})?`)) handleDeactivate(vehicle._id); }} disabled={actionLoading} className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50">Deactivate</button>
                        )}
                        {vehicle.approvalStatus === 'approved' && vehicle.status === 'inactive' && (
                          <button onClick={() => { if (window.confirm(`Reactivate ${vehicle.vehicleMake} ${vehicle.model} (${vehicle.licensePlate})?`)) handleReactivate(vehicle._id); }} disabled={actionLoading} className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-50">Reactivate</button>
                        )}
                        {(vehicle.status === 'inactive' || vehicle.approvalStatus === 'rejected') && (
                          <button onClick={() => handleDelete(vehicle._id)} disabled={actionLoading} className="text-red-500 hover:text-red-700 disabled:opacity-50" title="Remove"><Trash2 size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Vehicle Modal */}
      {selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{selectedVehicle.year} {selectedVehicle.vehicleMake} {selectedVehicle.model}</h3>
              <button onClick={() => { setSelectedVehicle(null); setActionNotes(''); }} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">License Plate</p><p className="font-medium font-mono">{selectedVehicle.licensePlate}</p></div>
                <div><p className="text-sm text-gray-500">Type</p><p className="font-medium capitalize">{selectedVehicle.vehicleType}</p></div>
                <div><p className="text-sm text-gray-500">Color</p><p className="font-medium">{selectedVehicle.color}</p></div>
                <div><p className="text-sm text-gray-500">Capacity</p><p className="font-medium">{selectedVehicle.capacity} passengers</p></div>
                <div><p className="text-sm text-gray-500">Approval</p><StatusBadge status={selectedVehicle.approvalStatus || 'pending'} /></div>
                <div><p className="text-sm text-gray-500">Status</p><StatusBadge status={selectedVehicle.status || 'active'} /></div>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none" rows={2} placeholder="Add notes..." />
                <div className="flex space-x-3 mt-3">
                  {selectedVehicle.approvalStatus === 'pending' && (
                    <>
                      <button onClick={() => handleApproval(selectedVehicle._id, 'approved')} disabled={actionLoading} className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 text-sm">Approve</button>
                      <button onClick={() => handleApproval(selectedVehicle._id, 'rejected')} disabled={actionLoading} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm">Reject</button>
                    </>
                  )}
                  {selectedVehicle.approvalStatus === 'approved' && selectedVehicle.status === 'active' && (
                    <button onClick={() => handleDeactivate(selectedVehicle._id)} disabled={actionLoading} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm">Deactivate</button>
                  )}
                  {selectedVehicle.approvalStatus === 'approved' && selectedVehicle.status === 'inactive' && (
                    <button onClick={() => handleReactivate(selectedVehicle._id)} disabled={actionLoading} className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 text-sm">Reactivate</button>
                  )}
                  {(selectedVehicle.status === 'inactive' || selectedVehicle.approvalStatus === 'rejected') && (
                    <button onClick={() => handleDelete(selectedVehicle._id)} disabled={actionLoading} className="flex-1 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 font-medium disabled:opacity-50 text-sm">Remove</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== Payouts Tab ====================
const CompanyPayoutsTab = ({ companyId }) => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AdminPayoutAPI.getPending({ companyId, limit: 100 });
      if (response.data.success) {
        setPayouts(response.data.data.payouts);
      }
    } catch (error) {
      console.error('Fetch payouts error:', APIUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchPayouts(); }, [fetchPayouts]);

  const handleFail = async (payoutId) => {
    const reason = prompt('Enter failure reason:');
    if (!reason) return;
    setActionLoading(payoutId);
    try {
      await AdminPayoutAPI.fail(payoutId, reason);
      fetchPayouts();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div></div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {payouts.length === 0 ? (
        <EmptyState title="No pending payouts" message="This company has no pending or processing payouts." icon={DollarSign} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {payouts.map((payout) => (
                <tr key={payout._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-mono text-gray-700">{payout.referenceNumber}</td>
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-900">
                      {payout.recipientType === 'driver'
                        ? `${payout.driver?.name} ${payout.driver?.surname}`
                        : payout.company?.companyName}
                    </p>
                    {/* Zuther: only "Mark paid" records an EFT (#383). A payout listed here is an older record: it can only be failed. */}
                    <p className="text-xs text-gray-500">An older payout record. Pay operators with "Mark paid" on the weekly statement; fail this one to close it.</p>
                  </td>
                  <td className="px-6 py-4"><StatusBadge status={payout.recipientType} /></td>
                  <td className="px-6 py-4 font-medium text-gray-900">R{(payout.amount / 100).toFixed(2)}</td>
                  <td className="px-6 py-4"><StatusBadge status={payout.status} /></td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button onClick={() => handleFail(payout._id)} disabled={actionLoading === payout._id} className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50">Fail</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CompanyDetail;
